/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

// Round manually typed cart quantities to the nearest box multiple for box_only tile products.
// The step attribute is set in Liquid to per_box_qty; this ensures the value is corrected
// before theme.js reads and sends it to the Cart API.
document.addEventListener('change', (event) => {
  const input = event.target.closest('.quantity-selector__value');
  if (!input) return;
  const step = parseInt(input.step, 10);
  if (!step || step <= 1) return;
  const val = parseInt(input.value, 10) || step;
  const rounded = Math.ceil(val / step) * step;
  if (rounded !== val) input.value = rounded;
});

// Calculator swatch picker: the picker is rendered inside the `calculator` block, which the
// theme's <product-rerender> excludes from partial re-render (to preserve the typed quantity).
// That re-render is what normally refreshes the "Selected: …" text, so here we update it
// ourselves on swatch change. The selected ring is handled by CSS (:checked + label), and the
// price refresh by the theme's own variant:changed flow — this only touches the label text.
document.addEventListener('change', (event) => {
  const radio = event.target.closest('.variant-swatch-picker__radio');
  if (!radio) return;
  const option = radio.closest('.product-form__option');
  const valueEl = option && option.querySelector('.product-form__selected-value');
  if (valueEl) valueEl.textContent = radio.dataset.swatchValue || '';
});

// =============================================================================
// BANK HOLIDAYS — England & Wales
// Update annually: https://www.gov.uk/bank-holidays (England tab)
// Single point of reference — also read by snippets/delivery-cutoff.liquid's
// countdown (as the global window.BANK_HOLIDAYS, since this script loads `defer`).
// Update once here; don't duplicate a holiday list anywhere else in the theme.
// =============================================================================
var BANK_HOLIDAYS = [
  '2025-08-25', '2025-12-25', '2025-12-26',
  '2026-01-01', '2026-04-03', '2026-04-06',
  '2026-05-04', '2026-05-25', '2026-08-31',
  '2026-12-25', '2026-12-28',
  '2027-01-01', '2027-03-26', '2027-03-29',
  '2027-05-03', '2027-05-31', '2027-08-30',
  '2027-12-27', '2027-12-28'
];
// =============================================================================

// Show the "Showroom Open" phone block only during opening hours in UK time.
// Mon–Sat, 09:00–16:59, excluding bank holidays.
(function () {
  var ukNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }));
  var day  = ukNow.getDay();
  var hour = ukNow.getHours();
  var pad  = function (n) { return String(n).padStart(2, '0'); };
  var today = ukNow.getFullYear() + '-' + pad(ukNow.getMonth() + 1) + '-' + pad(ukNow.getDate());

  if (day >= 1 && day <= 6 && hour >= 9 && hour < 17 && BANK_HOLIDAYS.indexOf(today) === -1) {
    ['showroom-open', 'showroom-call'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.style.display = '';
    });
  }
}());

// =============================================================================
// SELF-CORRECTING CART PROPERTIES
// Shopify freezes a line's visible properties (SQM (QTY) / Tiles / Box's) when
// only its quantity changes, so admin order views and notification emails show
// stale numbers (docs/RESOLVED-stale-order-properties.md). After every cart
// rerender, re-sync the visible properties of tile lines to the live quantity
// via /cart/change.js, mirroring snippets/tile-cart-properties.liquid.
//
// Cart API semantics (probed 2026-07-07): /cart/change.js replaces properties
// wholesale and re-keys the line; a properties-only POST resets quantity to 1,
// so quantity MUST be sent — hence the debounce + fresh-fetch + verify passes
// to avoid clobbering a quantity the customer changed mid-correction.
// =============================================================================
(function () {
  var DEBOUNCE_MS = 500;
  var MAX_PASSES = 3; // initial correction + up to 2 verify-after-write retries
  var debounceTimer = null;
  var correcting = false;

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); });
  }

  function isStaleTileLine(item) {
    var p = item.properties || {};
    var isTile = Number(p['_sqm_per_tile']) > 0 || Number(p['_linear_m_per_tile']) > 0;
    return isTile && Number(p['Tiles']) !== item.quantity;
  }

  function round2(n) {
    return Math.round((n + Number.EPSILON) * 100) / 100;
  }

  // Mirrors snippets/tile-cart-properties.liquid: unit is SQM when
  // _sqm_per_tile > 0, else Linear M; Tiles tracks quantity; Box's only for
  // box-only lines. Values are sent as strings to match what a fresh
  // calculator add would write.
  function correctedProperties(item) {
    var props = Object.assign({}, item.properties);
    var qty = item.quantity;
    var sqm = Number(props['_sqm_per_tile']);
    var linear = Number(props['_linear_m_per_tile']);
    var perBox = Number(props['_per_box_qty']);

    if (sqm > 0) {
      props['SQM (QTY)'] = String(round2(qty * sqm));
    } else if (linear > 0) {
      props['Linear M (QTY)'] = String(round2(qty * linear));
    }
    props['Tiles'] = String(qty);
    if (props['_box_only'] === 'true' && perBox > 0) {
      props["Box's"] = String(Math.ceil(qty / perBox));
    }
    return props;
  }

  function changeLine(item) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: item.key,
        quantity: item.quantity,
        properties: correctedProperties(item)
      })
    }).then(function (res) {
      if (!res.ok) throw new Error('/cart/change.js ' + res.status);
      return res.json();
    });
  }

  // One pass: fetch a fresh cart snapshot immediately before building POSTs,
  // correct stale lines sequentially, then recurse as the verify-after-write
  // step (property changes re-key lines and can merge same-variant lines).
  // Resolves true if anything was corrected. Shows the theme's loading state
  // while corrections are in flight so the drawer discourages interaction.
  function correctionPass(passesLeft, loadingShown) {
    return fetchCart().then(function (cart) {
      var staleLines = cart.items.filter(isStaleTileLine);
      if (staleLines.length === 0 || passesLeft <= 0) {
        if (loadingShown) {
          // Nothing left to correct, but corrections happened on an earlier
          // recursion — the completion cart:refresh ends the loading state.
          return true;
        }
        return false;
      }

      if (!loadingShown) {
        document.dispatchEvent(new CustomEvent('theme:loading:start'));
      }

      var chain = Promise.resolve();
      staleLines.forEach(function (item) {
        chain = chain.then(function () {
          // A failed line (e.g. 400 on a key re-keyed mid-pass by a merge)
          // must not abort the pass — the verify pass picks it up.
          return changeLine(item).catch(function (err) {
            console.warn('[cart-properties] line correction failed', item.key, err);
          });
        });
      });

      return chain.then(function () {
        return correctionPass(passesLeft - 1, true);
      });
    });
  }

  function runCorrection() {
    correcting = true;
    debounceTimer = null;
    correctionPass(MAX_PASSES, false)
      .then(function (didCorrect) {
        if (didCorrect) {
          // Dispatch on document, NOT a bubbling dispatch on documentElement:
          // the theme listens for cart:refresh both via a delegate on
          // documentElement and directly on document, and reaching both would
          // double-fire the rerender. The theme dispatches theme:loading:end
          // after this refresh completes.
          document.dispatchEvent(new CustomEvent('cart:refresh', {
            detail: { scrollToTop: false }
          }));
        }
      })
      .catch(function (err) {
        console.warn('[cart-properties] correction pass failed', err);
        // The refresh that would normally end the loading state never comes.
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      })
      .finally(function () {
        correcting = false;
      });
  }

  function scheduleCorrection() {
    if (correcting) return;
    if (debounceTimer !== null) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runCorrection, DEBOUNCE_MS);
  }

  // Capture phase is required: the drawer rerender event does not bubble;
  // only the cart-page one does.
  document.addEventListener('cart:rerendered', scheduleCorrection, true);

  // theme:loading:start fires synchronously on every quantity click, before
  // the theme's own /cart/change.js POST — restarting a pending debounce here
  // closes the click-to-rerender gap where a correction could revert a click
  // the cart:rerendered debounce hasn't seen yet. Only restarts an existing
  // timer; it never schedules corrections for unrelated loading events.
  document.addEventListener('theme:loading:start', function () {
    if (correcting || debounceTimer === null) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runCorrection, DEBOUNCE_MS);
  });
}());

// Samples app button: the app block's text is a single global setting, so we
// rewrite it client-side from the custom.sample_size / sample_price metafields
// (output as JSON by product-info.liquid — absent when either metafield is
// empty, leaving the app's default text as the fallback). A persistent
// MutationObserver re-applies the text if the app injects late or the theme
// fully re-renders the product info; the differs-check prevents observer loops.
(function () {
  function applySampleButtonText() {
    document.querySelectorAll('[product-samples-button]').forEach(function (button) {
      var form = button.closest('form');
      var dataEl = form && form.querySelector('[data-sample-button-info]');
      if (!dataEl) return;
      var info;
      try { info = JSON.parse(dataEl.textContent); } catch (e) { return; }
      if (!info.size || !info.price) return;
      var text = 'Order a ' + info.size + ' sample for only ' + info.price;
      if (button.textContent !== text) button.textContent = text;
    });
  }
  applySampleButtonText();
  new MutationObserver(applySampleButtonText).observe(document.body, { childList: true, subtree: true });
}());
